import { useState } from "react";
import { useStripe } from "@stripe/react-stripe-js";

import { captureException } from "common/utils/monitoring";
import { deepCombineErrors } from "common/utils/errors";
import { SubscriptionPriceSummary } from "common/types/priceSummary";
import { PaymentMethodOption, UISubscriptionStatus } from "common/types";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useClientQuery } from "client/accounts/hooks";
import PaymentMethodSelectPanel from "client/payments/components/PaymentMethodSelectPanel";
import { getWalletPaymentItems, walletDisplayText } from "client/payments/utils";
import { useUpdateSubscriptionPaymentMethodMutation } from "client/subscriptions/hooks";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import { useSubscriptionPaymentUpdateConfirmationDialog } from "client/subscriptions/stackables";
import { usePaymentCreateDrawer } from "client/payments/stackables";
import CardPaymentDeclineAlert from "client/common/components/content/CardPaymentDeclineAlert";
import {
  useCreatePaymentMethodMutation,
  useWalletPaymentMethodRequest,
} from "client/payments/hooks";

import { DetailSubscription } from "../../types";

export type SubscriptionPaymentUpdateDrawerProps = {
  subscription: DetailSubscription;
};

const SubscriptionPaymentUpdateDrawer = ({
  subscription,
}: SubscriptionPaymentUpdateDrawerProps) => {
  const { close } = useCurrentDrawer();
  const { open: openSubscriptionPaymentUpdateConfirmation } =
    useSubscriptionPaymentUpdateConfirmationDialog();
  const { open: openPaymentCreateDrawer } = usePaymentCreateDrawer();
  const { data: client, isLoading: isClientLoading, error: clientError } = useClientQuery();

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodOption | null>(
    subscription.payment_method ?? null,
  );
  const [originalSubscriptionStatus, setSubscriptionStatus] = useState(subscription.visible_status);

  const stripe = useStripe();
  const {
    mutate: updateSubscriptionPayment,
    isLoading: isUpdating,
    error: paymentUpdateError,
    reset: resetPaymentUpdate,
  } = useUpdateSubscriptionPaymentMethodMutation(subscription.id, {
    onSuccess: (data) => {
      if (
        data.visible_status === UISubscriptionStatus.Active &&
        originalSubscriptionStatus === UISubscriptionStatus.FailedPayment
      ) {
        openSubscriptionPaymentUpdateConfirmation({ subscription: data, onClose: close });
      } else {
        close();
      }
      setSubscriptionStatus(data.visible_status);
    },
    onError: (error, data) => {
      if (!stripe || !error.data?.stripe_action_client_secret) {
        return;
      }
      stripe.handleCardAction(error.data?.stripe_action_client_secret).then((result) => {
        if (result.paymentIntent) {
          updateSubscriptionPayment(data);
        }
      });
    },
  });

  const {
    mutateAsync: createWalletPaymentMethod,
    isLoading: isCreatingWalletPaymentMethod,
    error: createWalletPaymentMethodError,
  } = useCreatePaymentMethodMutation();

  const {
    request: walletPaymentRequest,
    supportedWallets,
    isLoading: isLoadingWalletPaymentRequest,
  } = useWalletPaymentMethodRequest({
    label: `Subscription ${subscription.display_number}`,
    amount: subscription.next_order_data.total,
    paymentItems: getWalletPaymentItems<SubscriptionPriceSummary>({
      ...subscription.next_order_data,
      tax: null,
      is_tax_final: false,
    }),
    onCreate: async (id) => {
      resetPaymentUpdate(); // reset the update subscription payment error
      const method = await createWalletPaymentMethod({ payment_method: id });
      updateSubscriptionPayment({
        payment_method_id: method.id,
      });
    },
  });

  if (isClientLoading) {
    return <DrawerLayout.Loading />;
  }

  if (!client || clientError) {
    return <DrawerLayout.FetchError entityName="payment method" error={clientError} />;
  }

  const cardDeclineError = paymentUpdateError?.data?.card_decline_info;

  const canSubmit = !!(paymentMethod && !isCreatingWalletPaymentMethod && !isUpdating);

  const handleSubmit = () => {
    if (!paymentMethod) {
      return;
    }

    if (paymentMethod.id === "__wallet__") {
      walletPaymentRequest?.show();
      return;
    }

    if (typeof paymentMethod.id === "number") {
      updateSubscriptionPayment({ payment_method_id: paymentMethod.id });
      return;
    }

    captureException(
      new Error(
        `Unexpected placeholder payment method [${JSON.stringify(paymentMethod)}] in SubscriptionPaymentUpdateDrawer`,
      ),
    );
  };

  const formError = createWalletPaymentMethodError || paymentUpdateError;

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title="Update payment method"
        description="This will update the payment method used to pay for this Autoship. All other Autoships will not be affected. Your card may be pre-authorized with a temporary $1.00 hold that will be removed within 7 business days."
        onClose={close}
      />
      <DrawerLayout.Form
        submitText={
          paymentMethod?.id === "__wallet__"
            ? `Authorize ${walletDisplayText(paymentMethod.wallet)} payment`
            : "Authorize Payment"
        }
        canSubmit={canSubmit}
        onSubmit={handleSubmit}
        // don't render the error in the default manner if we have a card decline error to show
        error={cardDeclineError ? null : formError}
        fieldErrors={[deepCombineErrors(formError?.errors)]}
        isLoading={isCreatingWalletPaymentMethod || isUpdating}
      >
        {cardDeclineError && <CardPaymentDeclineAlert declineError={cardDeclineError} />}
        <PaymentMethodSelectPanel
          initialValue={subscription.payment_method}
          value={paymentMethod ?? subscription.payment_method}
          options={client.payment_methods}
          walletPayment={supportedWallets}
          isLoading={isLoadingWalletPaymentRequest}
          onChange={setPaymentMethod}
          onAdd={() => openPaymentCreateDrawer({ onSuccess: (method) => setPaymentMethod(method) })}
        />
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

export default SubscriptionPaymentUpdateDrawer;
